import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { TaskTemplate } from "@shared/schema";

const taskTemplateFormSchema = z.object({
  taskName: z.string().min(1, "Task name is required"),
  taskDescription: z.string().optional(),
  targetDuration: z.string().min(1, "Target duration is required"),
  formName: z.string().optional(),
  url: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  tags: z.string().optional(), // comma-separated tags
  isActive: z.boolean().default(true),
});

type TaskTemplateFormData = z.infer<typeof taskTemplateFormSchema>;

interface TaskTemplateFormModalProps {
  open: boolean;
  onClose: () => void;
  template?: TaskTemplate | null;
}

export function TaskTemplateFormModal({ open, onClose, template }: TaskTemplateFormModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!template;

  const form = useForm<z.input<typeof taskTemplateFormSchema>, unknown, TaskTemplateFormData>({
    resolver: zodResolver(taskTemplateFormSchema),
    defaultValues: {
      taskName: "",
      taskDescription: "",
      targetDuration: "",
      formName: "",
      url: "",
      tags: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (template) {
      form.reset({
        taskName: template.taskName,
        taskDescription: template.taskDescription || "",
        targetDuration: template.targetDuration || "",
        formName: template.formName || "",
        url: template.url || "",
        tags: template.tags?.join(", ") || "",
        isActive: template.isActive ?? true,
      });
    } else {
      form.reset({
        taskName: "",
        taskDescription: "",
        targetDuration: "",
        formName: "",
        url: "",
        tags: "",
        isActive: true,
      });
    }
  }, [template, form]);

  const createMutation = useMutation({
    mutationFn: (data: TaskTemplateFormData) => {
      const payload = {
        ...data,
        tags: data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      return apiRequest('POST', '/api/task-templates', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/task-templates'] });
      toast({
        title: "Success",
        description: "Task template created successfully. You can create another or close this dialog.",
      });
      // Clear form but keep modal open for creating more tasks
      form.reset({
        taskName: "",
        taskDescription: "",
        targetDuration: "",
        formName: "",
        url: "",
        tags: "",
        isActive: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create task template",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: TaskTemplateFormData) => {
      const payload = {
        ...data,
        tags: data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      return apiRequest('PUT', `/api/task-templates/${template?.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/task-templates'] });
      toast({
        title: "Success",
        description: "Task template updated successfully",
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update task template",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TaskTemplateFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      form.reset();
      onClose();
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit' : 'Create'} Task Template</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="taskName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Name *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="e.g., Submit BOC-3 Form" 
                      {...field} 
                      data-testid="input-task-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taskDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter task description (optional)"
                      rows={3}
                      {...field}
                      data-testid="input-task-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="targetDuration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Duration *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., 2 days, 1 week" 
                        {...field}
                        data-testid="input-target-duration"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="formName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Form Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., BOC-3, UCR" 
                        {...field}
                        data-testid="input-form-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference URL</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="https://example.com/resource (optional)"
                      {...field}
                      data-testid="input-url"
                    />
                  </FormControl>
                  <FormDescription>
                    Link to instructions, forms, or related resources
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="e.g., compliance, filing, annual (comma-separated)"
                      {...field}
                      data-testid="input-tags"
                    />
                  </FormControl>
                  <FormDescription>
                    Enter tags separated by commas for easy filtering
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active</FormLabel>
                    <FormDescription>
                      Inactive templates won't appear in service template selections
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="switch-is-active"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-submit">
                {isPending ? (isEditing ? "Updating..." : "Creating...") : (isEditing ? "Update" : "Create")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
